import { ExclamationCircleFilled } from '@ant-design/icons'
import { TreeSelect as AntdTreeSelect } from 'antd'
import classNames from 'classnames'
import { useContext, useState } from 'react'
import { Controller, FieldErrors } from 'react-hook-form'

import type {
  AntdTreeType,
  CategoryInterface,
  NomenclatureGroupInterface,
  TreeSelectFormControlType,
} from '@/types'
import {
  getExpandedKeysForSearchValue,
  getExpandedKeysForValue,
} from '@/utils/tree/selection/treeExpandedKeys'

import {
  HoverElementContext,
  type HoverElementContextType,
} from '../HoverElement/HoverElement'
import cn from './AtomTreeSelect.module.scss'

type TreeSelectPropsType = {
  tree: NomenclatureGroupInterface[] | CategoryInterface[]
  antdTree: AntdTreeType[]
  openByHover?: boolean
  formName?: 'category' | 'parentId' | 'nomenclatureGroup'
  title?: string
  formControl?: TreeSelectFormControlType
  formErrors?: FieldErrors
  required?: boolean
  value?: string
  className?: string
  placeholder?: string
  disabled?: boolean
  onOpen?: () => void
  layout?: 'row' | 'stacked'
  isOrganisationSelected?: boolean
  handleChange: (
    tree: NomenclatureGroupInterface[] | CategoryInterface[],
    node: string
  ) => void
  handleClear: () => void
}

function AtomTreeSelect(props: TreeSelectPropsType) {
  const {
    tree,
    antdTree,
    openByHover = true,
    formName,
    title,
    formControl,
    formErrors,
    required,
    value,
    className,
    placeholder,
    disabled,
    onOpen,
    layout = 'row',
    isOrganisationSelected = false,
    handleChange,
    handleClear,
  } = props

  const isStacked = layout === 'stacked'
  const formErrorText =
    formErrors && formName && formErrors[formName]
      ? String(formErrors[formName].message)
      : ''
  const popupClassName = classNames(
    cn.treeSelectPopup,
    isOrganisationSelected && cn.treeSelectPopupSelected
  )

  const {
    open,
    containerRef,
    setOpen,
    handleMouseEnter,
    handleMouseLeaveContainer,
    handleSelectBlur,
  } = useContext(HoverElementContext) as HoverElementContextType

  const [openTreeSelect, setOpenTreeSelect] = useState<boolean>(false)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  return (
    <>
      {formControl && formName ? (
        <div className={classNames(cn.treeSelectItem, isStacked && cn.stacked)}>
          {title && (
            <label>
              {title}
              {isStacked && required && (
                <span className={cn.labelRequired} aria-hidden>
                  *
                </span>
              )}
            </label>
          )}
          <div
            className={classNames(
              cn.treeSelect,
              cn.inForm,
              formErrorText && cn.invalid,
              isOrganisationSelected && cn.accentSelected
            )}
          >
            <div
              ref={openByHover ? containerRef : undefined}
              onMouseLeave={openByHover ? handleMouseLeaveContainer : undefined}
            >
              <Controller
                name={formName}
                control={formControl}
                render={({ field }) => (
                  <AntdTreeSelect
                    {...field}
                    treeData={antdTree}
                    showSearch
                    placeholder={placeholder}
                    onOpenChange={(visible) => {
                      if (visible) {
                        setExpandedKeys(
                          getExpandedKeysForValue(tree, field.value as string)
                        )
                      }
                      if (visible) onOpen?.()
                      if (!openByHover) setOpenTreeSelect(visible)
                    }}
                    onChange={(value) => {
                      field.onChange(value)
                      setExpandedKeys(
                        getExpandedKeysForValue(tree, value as string)
                      )
                      if (!openByHover) {
                        setOpen(false)
                      } else {
                        setOpenTreeSelect(false)
                      }
                      handleChange(tree, value as string)
                    }}
                    treeNodeFilterProp="filterProp"
                    classNames={{
                      popup: {
                        root: popupClassName,
                      },
                    }}
                    allowClear
                    disabled={disabled}
                    notFoundContent={
                      <div className={cn.treeSelectNotFoundContent}>
                        <span>Ничего не найдено</span>
                      </div>
                    }
                    onSearch={(searchValue) => {
                      setExpandedKeys(
                        searchValue
                          ? getExpandedKeysForSearchValue(antdTree, searchValue)
                          : getExpandedKeysForValue(tree, field.value as string)
                      )
                    }}
                    treeExpandedKeys={expandedKeys}
                    onTreeExpand={(keys) => setExpandedKeys(keys as string[])}
                    open={openByHover ? open : openTreeSelect}
                    onMouseEnter={openByHover ? handleMouseEnter : undefined}
                    onBlur={openByHover ? handleSelectBlur : undefined}
                    onClear={() => {
                      field.onChange('')
                      setExpandedKeys([])
                      handleClear()
                    }}
                  />
                )}
              ></Controller>
              {required && !isStacked && (
                <span className={cn.requiredMark}>*</span>
              )}
            </div>
            <span
              role={isStacked ? 'alert' : undefined}
              title={isStacked && formErrorText ? formErrorText : undefined}
              className={classNames(cn.formError, !formErrorText && cn.hidden)}
            >
              {isStacked && formErrorText && (
                <ExclamationCircleFilled className={cn.errorIcon} />
              )}
              {formErrorText || (isStacked ? '' : 'нет')}
            </span>
          </div>
        </div>
      ) : (
        <div
          className={classNames(
            cn.treeSelect,
            isOrganisationSelected && cn.accentSelected,
            className
          )}
        >
          <div
            ref={openByHover ? containerRef : undefined}
            onMouseLeave={openByHover ? handleMouseLeaveContainer : undefined}
          >
            <AntdTreeSelect
              treeData={antdTree}
              showSearch
              treeNodeFilterProp="filterProp"
              classNames={{
                popup: {
                  root: popupClassName,
                },
              }}
              allowClear
              disabled={disabled}
              placeholder={placeholder}
              onOpenChange={(visible) => {
                if (visible) {
                  setExpandedKeys(getExpandedKeysForValue(tree, value))
                }
                if (visible) onOpen?.()
                if (!openByHover) setOpenTreeSelect(visible)
              }}
              onChange={(value) => {
                setExpandedKeys(getExpandedKeysForValue(tree, value))
                if (!openByHover) {
                  setOpen(false)
                } else {
                  setOpenTreeSelect(false)
                }
                handleChange(tree, value)
              }}
              notFoundContent={
                <div className={cn.treeSelectNotFoundContent}>
                  <span>Ничего не найдено</span>
                </div>
              }
              onSearch={(searchValue) => {
                setExpandedKeys(
                  searchValue
                    ? getExpandedKeysForSearchValue(antdTree, searchValue)
                    : getExpandedKeysForValue(tree, value)
                )
              }}
              treeExpandedKeys={expandedKeys}
              onTreeExpand={(keys) => setExpandedKeys(keys as string[])}
              value={value || undefined}
              open={openByHover ? open : openTreeSelect}
              onMouseEnter={openByHover ? handleMouseEnter : undefined}
              onBlur={openByHover ? handleSelectBlur : undefined}
              onClear={() => {
                setExpandedKeys([])
                handleClear()
              }}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default AtomTreeSelect
